/**
 * Keeps sign-in tokens out of what is sent to Sentry. A sign-in link or password reset can sit in the
 * page's address for a moment, and Sentry copies addresses into the report, its Referer header and the
 * breadcrumbs. So the query and fragment of every address are cut, anywhere in the event, and anything
 * still shaped like a token is removed. If this fails the event is dropped: a lost report is better
 * than a leaked token. Nutrition has the same in src/utils/sentryScrub.ts.
 */
const REMOVED = '[removed]';
const MAX_DEPTH = 20;

const DROPPED_KEYS = new Set(['query_string', 'fragment', 'http.query', 'http.fragment']);

const ADDRESS_TAIL = /((?:https?:\/\/|\/)[^\s"'<>?#]*)[?#][^\s"'<>]*/gi;
const TOKEN_PARAM = /\b(access_token|refresh_token|provider_token|provider_refresh_token|id_token|token_hash|token|code)=[^&#\s"'<>]*/gi;
const JWT = /\beyJ[\w-]+\.[\w-]+\.[\w-]+/g;

const scrubText = (text) =>
  text.replace(ADDRESS_TAIL, '$1').replace(TOKEN_PARAM, `$1=${REMOVED}`).replace(JWT, REMOVED);

const isPlain = (value) => {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

const scrub = (value, depth) => {
  if (typeof value === 'string') return scrubText(value);
  if (value === null || typeof value !== 'object') return value;
  if (depth > MAX_DEPTH) return REMOVED;
  if (Array.isArray(value)) return value.map((item) => scrub(item, depth + 1));
  if (!isPlain(value)) return value instanceof Date ? value : REMOVED;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => !DROPPED_KEYS.has(key))
      .map(([key, item]) => [key, scrub(item, depth + 1)])
  );
};

// For Sentry's beforeSend and beforeSendTransaction
export const scrubAddresses = (event) => {
  try {
    return scrub(event, 0);
  } catch {
    return null;
  }
};
